// Terms of Use page
import { createElement } from '@/utils/dom'
import { router } from '@/utils/router'
import { Footer } from '@/components/footer'

export class TermosDeUsoPage {

  async render(): Promise<HTMLElement> {
    const page = createElement('div', { className: 'legal-page' })

    // Navigation
    page.appendChild(this.createNavigation())
    
    // Main content
    page.appendChild(this.createMainContent())
    
    // Footer
    const footer = new Footer()
    page.appendChild(footer.render())

    return page
  }

  private createNavigation(): HTMLElement {
    const nav = createElement('nav', { className: 'nav' })
    
    nav.innerHTML = `
      <div class="container">
        <div class="nav-container">
          <a href="#" class="nav-logo">
            🗺️ Mapas KM
          </a>
          <ul class="nav-menu">
            <li><a href="/" class="nav-link">Início</a></li>
            <li><a href="/pricing" class="nav-link">Preços</a></li>
            <li><a href="/funcionalidades" class="nav-link">Funcionalidades</a></li>
            <li><a href="/register" class="nav-cta">Começar Grátis →</a></li>
          </ul>
        </div>
      </div>
    `

    // Add click handlers
    nav.addEventListener('click', (e) => {
      e.preventDefault()
      const target = e.target as HTMLElement
      
      if (target.tagName === 'A') {
        const href = target.getAttribute('href')
        if (href && href !== '#') {
          router.navigate(href)
        }
      }
    })

    return nav
  }

  private createMainContent(): HTMLElement {
    const main = createElement('main', { className: 'legal-content' })
    
    main.innerHTML = `
      <div class="container">
        <div class="legal-header">
          <h1>Termos de Uso</h1>
          <p class="legal-subtitle">Última atualização: 15 de agosto de 2025</p>
        </div>

        <div class="legal-body">
          <section class="legal-section">
            <h2>1. Aceitação dos Termos</h2>
            <p>Ao aceder e utilizar a plataforma Mapas KM ("Serviço"), concorda estar vinculado por estes Termos de Uso. Se não concorda com qualquer parte destes termos, não deve utilizar o nosso Serviço.</p>
          </section>

          <section class="legal-section">
            <h2>2. Descrição do Serviço</h2>
            <p>O Mapas KM é uma aplicação web que simula rotas de viagem empresariais comuns de funcionários de empresas, ajudando empresas portuguesas a automatizar a análise de padrões de viagem e otimização de rotas para fins de relatórios de despesas empresariais.</p>
            
            <h3>2.1 Funcionalidades Principais</h3>
            <ul>
              <li>Geração automática de rotas de viagem realistas</li>
              <li>Cálculos de distância baseados em estradas reais</li>
              <li>Exportação de relatórios profissionais em Excel</li>
              <li>Sistema de cache para redução de custos de API</li>
              <li>Gestão segura de dados da empresa</li>
            </ul>
          </section>

          <section class="legal-section">
            <h2>3. Elegibilidade e Registo</h2>
            
            <h3>3.1 Elegibilidade</h3>
            <ul>
              <li>Deve ser uma empresa legalmente constituída em Portugal</li>
              <li>Deve possuir um NIF válido</li>
              <li>Deve ter autoridade para aceitar estes termos em nome da sua empresa</li>
            </ul>

            <h3>3.2 Informações de Registo</h3>
            <ul>
              <li>Nome da empresa</li>
              <li>Email de contacto válido</li>
              <li>NIF (Número de Identificação Fiscal)</li>
              <li>Dados do administrador responsável</li>
            </ul>

            <h3>3.3 Veracidade das Informações</h3>
            <p>Garante que todas as informações fornecidas são verdadeiras, precisas e atualizadas. É responsável por manter essas informações atualizadas.</p>
          </section>

          <section class="legal-section">
            <h2>4. Uso Aceitável</h2>
            
            <h3>4.1 Uso Permitido</h3>
            <ul>
              <li>Gerar relatórios de despesas para fins empresariais legítimos</li>
              <li>Exportar dados para sistemas internos de contabilidade</li>
              <li>Partilhar relatórios com contabilistas ou autoridades fiscais conforme necessário</li>
            </ul>

            <h3>4.2 Uso Proibido</h3>
            <ul>
              <li>Usar o Serviço para atividades ilegais ou fraudulentas</li>
              <li>Tentar aceder a dados de outras empresas</li>
              <li>Fazer engenharia reversa ou copiar o Serviço</li>
              <li>Sobrecarregar o sistema com pedidos excessivos</li>
              <li>Partilhar credenciais de acesso com terceiros</li>
            </ul>
          </section>

          <section class="legal-section">
            <h2>5. Propriedade Intelectual</h2>
            
            <h3>5.1 Direitos do Mapas KM</h3>
            <p>Todos os direitos, títulos e interesses no Serviço, incluindo software, algoritmos, interface e documentação, são propriedade exclusiva do Mapas KM.</p>

            <h3>5.2 Licença de Uso</h3>
            <p>Concedemos uma licença limitada, não exclusiva e revogável para usar o Serviço de acordo com estes termos.</p>

            <h3>5.3 Dados do Cliente</h3>
            <p>Mantém a propriedade de todos os dados que carrega ou gera através do Serviço.</p>
          </section>

          <section class="legal-section">
            <h2>6. Pagamento e Faturação</h2>
            
            <h3>6.1 Planos de Subscrição</h3>
            <ul>
              <li><strong>Starter</strong>: €29/mês - 50 relatórios</li>
              <li><strong>Pro</strong>: €79/mês - 200 relatórios</li>
              <li><strong>Enterprise</strong>: €199/mês - Relatórios ilimitados</li>
            </ul>

            <h3>6.2 Política de Pagamento</h3>
            <ul>
              <li>Faturação mensal ou anual antecipada</li>
              <li>Pagamentos processados de forma segura</li>
              <li>Sem reembolsos após geração de relatórios</li>
            </ul>

            <h3>6.3 Suspensão por Não Pagamento</h3>
            <p>Reservamo-nos o direito de suspender o acesso em caso de não pagamento, após notificação prévia de 7 dias.</p>
          </section>

          <section class="legal-section">
            <h2>7. Responsabilidades e Limitações</h2>
            
            <h3>7.1 Disponibilidade do Serviço</h3>
            <ul>
              <li>Esforçamo-nos por manter 99% de disponibilidade durante horário comercial</li>
              <li>Manutenção programada será comunicada com 24h de antecedência</li>
              <li>Não garantimos disponibilidade ininterrupta</li>
            </ul>

            <h3>7.2 Precisão dos Dados</h3>
            <ul>
              <li>Utilizamos APIs terceiras (Google Maps) para cálculos de distância</li>
              <li>Não garantimos 100% de precisão geográfica</li>
              <li>Relatórios destinam-se a fins de estimativa empresarial</li>
            </ul>

            <h3>7.3 Limitação de Responsabilidade</h3>
            <p>O Mapas KM não será responsável por:</p>
            <ul>
              <li>Perdas indiretas ou consequenciais</li>
              <li>Decisões financeiras baseadas nos relatórios</li>
              <li>Problemas com autoridades fiscais</li>
              <li>Interrupções de serviços terceiros</li>
            </ul>
          </section>

          <section class="legal-section">
            <h2>8. Proteção de Dados</h2>
            
            <h3>8.1 Conformidade RGPD</h3>
            <ul>
              <li>Processamos dados pessoais em conformidade com o RGPD</li>
              <li>Consulte a nossa <a href="/politica-de-privacidade" class="legal-link">Política de Privacidade</a> para detalhes</li>
              <li>Direito de acesso, retificação e eliminação de dados</li>
            </ul>

            <h3>8.2 Segurança</h3>
            <ul>
              <li>Cifragem de dados em trânsito e em repouso</li>
              <li>Autenticação segura com JWT</li>
              <li>Backups automáticos diários</li>
            </ul>
          </section>

          <section class="legal-section">
            <h2>9. Modificações do Serviço</h2>
            
            <h3>9.1 Atualizações</h3>
            <p>Reservamo-nos o direito de:</p>
            <ul>
              <li>Modificar funcionalidades do Serviço</li>
              <li>Atualizar preços com 30 dias de aviso prévio</li>
              <li>Descontinuar funcionalidades obsoletas</li>
            </ul>

            <h3>9.2 Notificações</h3>
            <p>Mudanças significativas serão comunicadas por email com pelo menos 15 dias de antecedência.</p>
          </section>

          <section class="legal-section">
            <h2>10. Terminação</h2>
            
            <h3>10.1 Terminação pelo Cliente</h3>
            <p>Pode cancelar a subscrição a qualquer momento através do painel de controlo ou contactando-nos.</p>

            <h3>10.2 Terminação pela Mapas KM</h3>
            <p>Podemos terminar o acesso imediatamente em caso de:</p>
            <ul>
              <li>Violação destes termos</li>
              <li>Uso fraudulento</li>
              <li>Não pagamento após período de graça</li>
            </ul>

            <h3>10.3 Efeitos da Terminação</h3>
            <ul>
              <li>Acesso ao Serviço será suspenso</li>
              <li>Dados podem ser retidos por 90 dias para recuperação</li>
              <li>Após 90 dias, dados serão eliminados permanentemente</li>
            </ul>
          </section>

          <section class="legal-section">
            <h2>11. Resolução de Conflitos</h2>
            
            <h3>11.1 Lei Aplicável</h3>
            <p>Estes termos regem-se pela lei portuguesa.</p>

            <h3>11.2 Jurisdição</h3>
            <p>Quaisquer disputas serão resolvidas nos tribunais competentes de Lisboa, Portugal.</p>

            <h3>11.3 Resolução Amigável</h3>
            <p>Encorajamos a resolução amigável de conflitos através de contacto direto antes de ações legais.</p>
          </section>

          <section class="legal-section">
            <h2>12. Contacto</h2>
            <p>Para questões sobre estes Termos de Uso:</p>
            <ul>
              <li><strong>Email:</strong> antoniolozadalda@gmail.com</li>
              <li><strong>Morada:</strong> RUA DO ALECRIM 21 6 ESQ</li>
            </ul>
          </section>

          <div class="legal-footer">
            <p>Ao utilizar o Mapas KM, confirma que leu, compreendeu e aceita estes Termos de Uso na íntegra.</p>
          </div>
        </div>
      </div>
    `

    // Add click handlers for internal links
    main.addEventListener('click', (e) => {
      e.preventDefault()
      const target = e.target as HTMLElement
      
      if (target.tagName === 'A' && target.classList.contains('legal-link')) {
        const href = target.getAttribute('href')
        if (href) {
          router.navigate(href)
        }
      }
    })

    return main
  }
}
